import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Mutex } from 'async-mutex'
import { AuthToken, AuthTokenArgs, Customer } from '@typesApp/Customer'
import { CustomerContext } from '@typesApp/Customer'
import { buildAuthTokenArgs } from '@utils/authToken'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { resetClaimStatusInitialState, setActiveClaimRecord } from '@redux/slices/claimStatusSlice'
import { authTokenError, connectorInitCustomer, connectorSetSessionInitializing } from '@redux/slices/connectorSlice'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { useGetAuthTokenMutation } from '@redux/queries/auth'
import { useEnvironment } from './useEnvironment'
import { useCustomer } from './useCustomer'
import { useInitTokenAction } from './useInitTokenAction'

type UseSessionReturn = {
  /**
   * Init auth token and active data
   */
  initSession: (context: CustomerContext, cleanupFunction?: () => void) => Promise<void>
}

const mutex = new Mutex()

/**
 * Initialize the session by fetching the commerce customer session and auth token if needed
 */
export const useSession = (): UseSessionReturn => {
  const dispatch = useDispatch()
  const { anonymousUserId, ecommerceCode, ecommerceTransactionHost } = useEnvironment()
  const { getCustomer } = useCustomer()
  const { beforeInitToken, afterInitToken } = useInitTokenAction()
  const [fetchAuthToken] = useGetAuthTokenMutation()

  const getAuthTokenArgs = useCallback(
    (customer: Customer, customerContext: CustomerContext): AuthTokenArgs => {
      if (!isCustomerNumberValid(customer?.id, anonymousUserId)) {
        throw new Error(`Cannot init auth token, invalid customer number ${customer?.id}`)
      }

      const ecommerce = formatCommerceCode({
        ecommerceCode,
        ecommerceTransactionHost,
      })

      return buildAuthTokenArgs(ecommerce, customer, customerContext)
    },
    [anonymousUserId, ecommerceCode, ecommerceTransactionHost]
  )

  const initToken = useCallback(
    async (customer: Customer, customerContext: CustomerContext): Promise<AuthToken> => {
      const args = getAuthTokenArgs(customer, customerContext)

      // reset active data state before we fetch token
      dispatch(resetClaimStatusInitialState())
      dispatch(claimRecordsApi.util.resetApiState())

      const authToken = await fetchAuthToken(args).unwrap()
      dispatch(setActiveClaimRecord(authToken?.claimRecordId))

      return authToken
    },
    [getAuthTokenArgs, dispatch, fetchAuthToken]
  )

  const initSession = useCallback(
    async (customerContext: CustomerContext, cleanupFunction?: () => void): Promise<void> => {
      dispatch(connectorSetSessionInitializing(true))

      try {
        await mutex.waitForUnlock()

        // fetch customer data and init auth token
        if (!mutex.isLocked()) {
          const releaseMutex = await mutex.acquire()
          try {
            const customer = await getCustomer()
            dispatch(connectorInitCustomer(customer))

            await beforeInitToken(customer, customerContext)
            const authToken = await initToken(customer, customerContext)
            await afterInitToken(customer, customerContext, authToken)
          } catch (error) {
            console.warn('directbilling session - error on init token', error)
            dispatch(authTokenError())
          } finally {
            releaseMutex()
          }
        }
      } finally {
        cleanupFunction?.()
        dispatch(connectorSetSessionInitializing(false))
      }
    },
    [dispatch, getCustomer, initToken, beforeInitToken, afterInitToken]
  )

  return {
    initSession,
  }
}
